import { ResponsiveBar } from "@nivo/bar";
import { format, startOfMonth, subMonths } from "date-fns";
import { Info, TrendingUp } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { memo, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { findDealLabel } from "../deals/dealUtils";
import { reportingAmountOf } from "../misc/currency";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";

const defaultMultiplier: Record<string, number> = {
  opportunity: 0.2,
  "proposal-sent": 0.5,
  "in-negociation": 0.8,
  delayed: 0.3,
  completed: 1.0,
};

const DEFAULT_LOCALE = "en-US";

const formatAxisValue = (v: number): string => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(abs / 1_000)}k`;
  return String(abs);
};

export const DealsChart = memo(({ compact = false }: { compact?: boolean }) => {
  const translate = useTranslate();
  const { dealStages, currency } = useConfigurationContext();
  const acceptedLanguages = navigator
    ? navigator.languages || [navigator.language]
    : [DEFAULT_LOCALE];
  const wonLabel = findDealLabel(dealStages, "won") ?? "Won";
  const lostLabel = findDealLabel(dealStages, "lost") ?? "Lost";

  // Computed at render time so it's fresh on each mount
  const sixMonthsAgo = useMemo(
    () => subMonths(new Date(), 6).toISOString(),
    [],
  );

  const { data, isPending } = useGetList<Deal>("deals", {
    pagination: { perPage: 100, page: 1 },
    sort: {
      field: "created_at",
      order: "ASC",
    },
    filter: {
      "created_at@gte": sixMonthsAgo,
    },
  });

  const months = useMemo(() => {
    if (!data) return [];
    const dealsByMonth = data.reduce(
      (acc, deal) => {
        const month = startOfMonth(
          deal.created_at ? new Date(deal.created_at) : new Date(),
        ).toISOString();
        if (!acc[month]) acc[month] = [];
        acc[month].push(deal);
        return acc;
      },
      {} as Record<string, Deal[]>,
    );

    return Object.keys(dealsByMonth).map((month) => ({
      date: format(month, "MMM"),
      won: dealsByMonth[month]
        .filter((deal) => deal.stage === "won")
        .reduce((acc, deal) => acc + reportingAmountOf(deal), 0),
      pending: dealsByMonth[month]
        .filter((deal) => !["won", "lost"].includes(deal.stage))
        .reduce((acc, deal) => {
          const rate = defaultMultiplier[deal.stage] ?? 0.5;
          return acc + reportingAmountOf(deal) * rate;
        }, 0),
      lost: dealsByMonth[month]
        .filter((deal) => deal.stage === "lost")
        .reduce((acc, deal) => acc - reportingAmountOf(deal), 0),
    }));
  }, [data]);

  if (isPending) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp
            className="text-nd-text-disabled w-4 h-4"
            strokeWidth={1.5}
          />
          <span className="font-nd-mono text-[11px] tracking-[0.08em] uppercase text-nd-text-secondary">
            {translate("crm.dashboard.deals_chart")}
          </span>
        </div>
        <div
          className={`${compact ? "h-[300px]" : "h-[400px]"} animate-pulse`}
          style={{ background: "var(--nd-surface-raised)", borderRadius: 4 }}
        />
      </div>
    );
  }

  if (!months.length) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp
            className="text-nd-text-disabled w-4 h-4"
            strokeWidth={1.5}
          />
          <span className="font-nd-mono text-[11px] tracking-[0.08em] uppercase text-nd-text-secondary">
            {translate("crm.dashboard.deals_chart")}
          </span>
        </div>
        <div
          className={`${compact ? "h-[300px]" : "h-[400px]"} flex items-center justify-center`}
        >
          <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
            {translate("crm.dashboard.deals_chart_empty", {
              _: "No deals in the last 6 months",
            })}
          </span>
        </div>
      </div>
    );
  }

  const range = months.reduce(
    (acc, month) => {
      acc.min = Math.min(acc.min, month.lost);
      acc.max = Math.max(acc.max, month.won + month.pending);
      return acc;
    },
    { min: 0, max: 0 },
  );

  // Prevent zero-range crash when all values are identical
  const rangeMin = range.min === range.max ? range.min - 1 : range.min;
  const rangeMax = range.min === range.max ? range.max + 1 : range.max;

  const monoFont = "var(--font-nd-mono)";
  const labelStyle = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    fill: "var(--nd-text-secondary)",
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp
          className="text-nd-text-disabled w-4 h-4"
          strokeWidth={1.5}
        />
        <span className="font-nd-mono text-[11px] tracking-[0.08em] uppercase text-nd-text-secondary flex-1">
          {translate("crm.dashboard.deals_chart")}
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 text-nd-text-disabled hover:text-nd-text-secondary"
            >
              <Info className="w-3 h-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-72 p-3 space-y-2"
            style={{ fontFamily: "var(--font-nd-mono)", fontSize: "11px" }}
            align="end"
          >
            <p>
              {translate("crm.dashboard.deals_chart_popover_intro", {
                _: "Deals are grouped by their opportunity date into monthly bars, covering the last 6 months.",
              })}
            </p>
            <p>
              <strong style={{ color: "var(--nd-text-display)" }}>
                {wonLabel}
              </strong>{" "}
              —{" "}
              {translate("crm.dashboard.deals_chart_popover_won", {
                _: "full deal amount, plotted above zero.",
              })}
            </p>
            <p>
              <strong style={{ color: "var(--nd-text-disabled)" }}>
                {translate("crm.dashboard.deals_pipeline", { _: "Pipeline" })}
              </strong>{" "}
              —{" "}
              {translate("crm.dashboard.deals_chart_popover_pipeline", {
                _: "weighted amount based on stage probability: Opportunity 20%, Delayed 30%, Proposal Sent 50%, In Negotiation 80%.",
              })}
            </p>
            <p>
              <strong style={{ color: "var(--nd-accent)" }}>{lostLabel}</strong>{" "}
              —{" "}
              {translate("crm.dashboard.deals_chart_popover_lost", {
                _: "full deal amount, plotted below zero.",
              })}
            </p>
            <p style={{ color: "var(--nd-text-disabled)" }}>
              {translate("crm.dashboard.deals_chart_popover_hint", {
                _: "Edit a deal's opportunity date to move it between months.",
              })}
            </p>
          </PopoverContent>
        </Popover>
        <div className="flex items-center gap-4">
          {[
            { color: "var(--nd-text-display)", label: wonLabel },
            { color: "var(--nd-text-disabled)", label: "Pipeline" },
            { color: "var(--nd-accent)", label: lostLabel },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2 h-2 rounded-sm flex-none"
                style={{ background: color }}
              />
              <span className="font-nd-mono text-[9px] tracking-[0.08em] uppercase text-nd-text-disabled">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className={compact ? "h-[300px]" : "h-[400px]"}>
        <ResponsiveBar
          data={months}
          indexBy="date"
          keys={["won", "pending", "lost"]}
          colors={[
            "var(--nd-text-display)",
            "var(--nd-text-disabled)",
            "var(--nd-accent)",
          ]}
          margin={
            compact
              ? { top: 36, right: 56, bottom: 24, left: 0 }
              : { top: 30, right: 56, bottom: 30, left: 0 }
          }
          animate={true}
          motionConfig={{ mass: 1, tension: 80, friction: 22 }}
          padding={0.35}
          valueScale={{
            type: "linear",
            min: rangeMin * 1.2,
            max: rangeMax * 1.2,
          }}
          indexScale={{ type: "band", round: true }}
          enableGridX={false}
          enableGridY={true}
          gridYValues={4}
          enableLabel={false}
          borderRadius={0}
          theme={{
            grid: {
              line: { stroke: "var(--nd-border)", strokeWidth: 1 },
            },
            axis: {
              ticks: { text: labelStyle },
              legend: { text: labelStyle },
            },
          }}
          tooltip={({ value, indexValue }) => (
            <div
              className="px-3 py-2 inline-flex items-center gap-1.5"
              style={{
                background: "var(--nd-surface-raised)",
                border: "1px solid var(--nd-border-visible)",
                borderRadius: "4px",
                fontFamily: monoFont,
                fontSize: "11px",
                color: "var(--nd-text-primary)",
              }}
            >
              <span
                style={{
                  color: "var(--nd-text-secondary)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {indexValue}
              </span>
              <span style={{ color: "var(--nd-text-display)" }}>
                {value > 0 ? "+" : ""}
                {value.toLocaleString(
                  acceptedLanguages.at(0) ?? DEFAULT_LOCALE,
                  { style: "currency", currency },
                )}
              </span>
            </div>
          )}
          axisTop={{
            tickSize: 0,
            tickPadding: 12,
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 12,
          }}
          axisLeft={null}
          axisRight={{
            format: (v: number) => formatAxisValue(v),
            tickValues: 4,
            tickSize: 0,
            tickPadding: 12,
          }}
          markers={
            [
              {
                axis: "y",
                value: 0,
                lineStyle: {
                  stroke: "var(--nd-border-visible)",
                  strokeWidth: 1,
                },
                textStyle: {
                  fill: "var(--nd-text-disabled)",
                  fontFamily: monoFont,
                  fontSize: 10,
                },
                legend: wonLabel,
                legendPosition: "top-left",
                legendOrientation: "vertical",
              },
              {
                axis: "y",
                value: 0,
                lineStyle: { strokeOpacity: 0 },
                textStyle: {
                  fill: "var(--nd-accent)",
                  fontFamily: monoFont,
                  fontSize: 10,
                },
                legend: lostLabel,
                legendPosition: "bottom-left",
                legendOrientation: "vertical",
              },
            ] as any
          }
        />
      </div>
    </div>
  );
});
